import styles from './Spiner.module.css';

import React from 'react';
import classNames from 'classnames';

/**
 * Компонент Spinner - индикатор загрузки с анимацией вращения
 *
 * @component
 * @param {Object} props - Свойства компонента
 * @param {'small' | 'medium' | 'large'} [props.size='medium'] - Предустановленный размер спиннера
 * @param {string | null} [props.customSize=null] - Пользовательский размер спиннера (например, '5rem' или '50px'). Имеет приоритет над size
 * @param {'primary' | 'secondary' | 'accent' | 'error'} [props.variant='primary'] - Цветовая схема спиннера
 * @param {boolean} [props.fullScreen=false] - Если true, спиннер будет отображаться поверх всего экрана с полупрозрачным фоном
 * @param {boolean} [props.overlay=false] - Если true, спиннер будет отображаться как оверлей для родительского контейнера (должен иметь position: relative)
 * @param {string} [props.text=''] - Текст, отображаемый под спиннером
 * @param {string} [props.className=''] - Дополнительные CSS классы для спиннера
 * @returns {React.ReactElement} Компонент индикатора загрузки
 *
 * @example
 * // Базовое использование
 * <Spinner />
 *
 * @example
 * // Спиннер с текстом
 * <Spinner text="Загрузка данных..." />
 *
 * @example
 * // Большой красный спиннер
 * <Spinner size="large" variant="error" />
 *
 * @example
 * // Полноэкранный спиннер
 * <Spinner fullScreen text="Загрузка приложения..." />
 *
 * @example
 * // Спиннер как оверлей для контейнера
 * <div style={{ position: 'relative', height: '300px' }}>
 *   <Spinner overlay text="Обновление..." />
 *   <div>Содержимое контейнера</div>
 * </div>
 *
 * @example
 * // Спиннер с пользовательским размером
 * <Spinner customSize="4.5rem" variant="accent" />
 */
const Spinner = ({
  size = 'medium',
  customSize = null,
  variant = 'primary',
  fullScreen = false,
  overlay = false,
  text = '',
  className = '',
}) => {
  const spinnerSizes = {
    small: '1.5rem',
    medium: '2rem',
    large: '3rem',
  };

  const spinnerStyle = {
    width: customSize ?? spinnerSizes[size],
    height: customSize ?? spinnerSizes[size],
  };

  const containerClasses = classNames(styles.container, {
    [styles.fullScreen]: fullScreen,
    [styles.overlay]: overlay && !fullScreen,
  });

  const spinnerClasses = classNames(
    styles.spinner,
    styles[`spinner${variant.charAt(0).toUpperCase() + variant.slice(1)}`],
    className,
  );

  return (
    <div className={containerClasses}>
      <div className={styles.content}>
        <div className={spinnerClasses} style={spinnerStyle} role="status">
          <span className={styles.visuallyHidden}>Загрузка...</span>
        </div>
        {text && <div className={styles.text}>{text}</div>}
      </div>
    </div>
  );
};

export default Spinner;
